import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import PrimaryButton from '@components/buttons/PrimaryButton';
import SecondaryButton from '@components/buttons/SecondaryButton';
import BaseErrors from '@components/fields/BaseErrors';
import DataSelect from '@components/fields/DataSelect';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import LocationAutocompleteInput from '@components/fields/LocationAutocompleteInput';
import RadioButtonGroup from '@components/fields/RadioButtonGroup';
import Select from '@components/fields/Select';
import Form from '@components/forms/Form';
import styles from '@components/forms/InitialConsultLegalForm.module.css';
import {
  CareProfileFragment as CareProfile,
  CareProfileAgeEnum,
  CareProfileMaritalStatusEnum,
  CareProfileReligionEnum,
  ClientRoleRelationshipEnum,
  GetJourneyWithInitialConsultQuery,
  InitialConsultReportUpdateDocument,
  InitialConsultReportUpdateMutation,
  InitialConsultReportUpdateMutationVariables
} from '@typing/Generated';
import { InitialConsultFields } from '@typing/Models';
import { handleMutationResponse } from '@utils/formUtils';
import { optionsForRelationship } from '@utils/optionUtils';

type FormData = {
  age: CareProfileAgeEnum | null;
  clientRoleRelationship: ClientRoleRelationshipEnum | null;
  culturalConsiderations: string | null;
  languageCode: string | null;
  maritalStatus: CareProfileMaritalStatusEnum | null;
  name: string | null;
  placeId: string | null;
  religion: CareProfileReligionEnum | null;
  religionOther: string | null;
};

type Props = {
  careProfile: CareProfile;
  client?: GetJourneyWithInitialConsultQuery['journey']['owner'];
  consultId: string | undefined | null;
  initialConsultReportId: string;
  journeyId: string;
  onCancel?: () => void;
  onSuccess: (nextPage?: string) => void;
  questions?: InitialConsultFields[];
};

const InitialConsultBasicInfoForm = ({
  careProfile,
  client,
  consultId,
  initialConsultReportId,
  journeyId,
  onCancel,
  onSuccess,
  questions
}: Props) => {
  const [submitted, setSubmitted] = useState(false);
  const [backOrNext, setBackOrNext] = useState<boolean | null>(null);

  useEffect(() => {
    if (backOrNext !== null && submitted) {
      if (backOrNext) {
        onSuccess('medical');
      } else {
        onSuccess('client-info');
      }
      setSubmitted(false);
      setBackOrNext(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backOrNext, submitted]);

  const clientRole = useMemo(
    () => client?.roles.find(role => role.journeyId === journeyId),
    [client?.roles, journeyId]
  );

  const initialValues: FormData = {
    age: careProfile?.age ?? null,
    clientRoleRelationship: clientRole?.relationship?.relationshipOption ?? null,
    culturalConsiderations: careProfile.culturalConsiderations ?? null,
    languageCode: careProfile.languageCode ?? null,
    maritalStatus: careProfile.maritalStatus ?? null,
    name: careProfile.name ?? null,
    placeId: import.meta.env.VITE_NO_GOOGLE_LOCATION_CALLS
      ? 'ChIJfX6-HwSbf4gR4Aa9pkeWBLY'
      : careProfile.location.placeId,
    religion: careProfile.religion?.religionOption ?? null,
    religionOther: careProfile.religion?.other ?? null
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateInitialConsultReport] = useMutation<
    InitialConsultReportUpdateMutation,
    InitialConsultReportUpdateMutationVariables
  >(InitialConsultReportUpdateDocument);
  const schema = yup
    .object()
    .shape({
      age: questions
        ? yup.string().nullable()
        : yup.string().required(intl.formatMessage({ id: 'errors.careProfile.age.blank' })),
      caregiverTypes: yup.array(),
      caregiverTypesOther: yup.string().nullable(),
      clientRoleRelationship: yup.string().nullable(),
      culturalConsiderations: yup.string().nullable(),
      languageCode: yup.string().nullable(),
      maritalStatus: yup.string().nullable(),
      name: questions
        ? yup.string().nullable()
        : yup.string().required(intl.formatMessage({ id: 'errors.careProfile.name.blank' })),
      placeId: questions
        ? yup.string().nullable()
        : yup.string().required(intl.formatMessage({ id: 'errors.careProfile.placeId.blank' })),
      religion: yup.string().nullable(),
      religionOther: yup.string().nullable()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const religion = form.watch('religion');

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return updateInitialConsultReport({
      variables: {
        ...form.getValues(),
        consultId,
        id: initialConsultReportId,
        religion: {
          other: religion === CareProfileReligionEnum.other ? form.getValues().religionOther : null,
          religionOption: form.getValues().religion
        }
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.initialConsultReportUpdate,
        intl,
        onSuccess: () => {
          if (!questions) {
            setSubmitted(true);
          } else {
            onSuccess();
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.careProfile'
      });
    });
  }, [consultId, form, initialConsultReportId, intl, onSuccess, questions, religion, updateInitialConsultReport]);

  const relationshipOptions = optionsForRelationship(intl, 'models.careProfile.relationship');

  const maritalStatusOptions = useMemo(() => {
    const options = [
      CareProfileMaritalStatusEnum.single,
      CareProfileMaritalStatusEnum.married,
      CareProfileMaritalStatusEnum.domestic_partner,
      CareProfileMaritalStatusEnum.divorced,
      CareProfileMaritalStatusEnum.separated,
      CareProfileMaritalStatusEnum.widowed,
      CareProfileMaritalStatusEnum.other
    ];
    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.initialConsultReport.questions.marital_status.options.${option}.content`
      }),
      value: option
    }));
  }, [intl]);

  const ageOptions = useMemo(() => {
    const options = [
      CareProfileAgeEnum.sixty_five_and_older,
      CareProfileAgeEnum.eighteen_to_sixty_four,
      CareProfileAgeEnum.under_eighteen,
      CareProfileAgeEnum.deceased
    ];
    return options.map(option => ({
      label: intl.formatMessage({ id: `models.careProfile.age.${option}` }),
      value: option
    }));
  }, [intl]);

  const religionOptions = useMemo(() => {
    const options = [
      CareProfileReligionEnum.none,
      CareProfileReligionEnum.atheism,
      CareProfileReligionEnum.bahai,
      CareProfileReligionEnum.buddhism,
      CareProfileReligionEnum.catholicism,
      CareProfileReligionEnum.christianity,
      CareProfileReligionEnum.hinduism,
      CareProfileReligionEnum.islam,
      CareProfileReligionEnum.jainism,
      CareProfileReligionEnum.jehovahs_witness,
      CareProfileReligionEnum.judaism,
      CareProfileReligionEnum.mormonism,
      CareProfileReligionEnum.other,
      CareProfileReligionEnum.paganism,
      CareProfileReligionEnum.rastafari,
      CareProfileReligionEnum.santeria,
      CareProfileReligionEnum.shinto,
      CareProfileReligionEnum.sikhism,
      CareProfileReligionEnum.spiritualism,
      CareProfileReligionEnum.taoism,
      CareProfileReligionEnum.unitarianism,
      CareProfileReligionEnum.zoroastrianism
    ];
    return options.map(option => ({
      label: intl.formatMessage({ id: `models.initialConsultReport.questions.religion.options.${option}.content` }),
      value: option
    }));
  }, [intl]);

  return (
    <Form data-test="report-basic-info-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div className={!questions || questions?.includes(InitialConsultFields.NAME) ? undefined : 'ion-hide'}>
        <Input<FormData>
          control={form.control}
          data-test="name-input"
          error={form.formState.errors.name}
          label={intl.formatMessage({ id: 'forms.careProfile.nameLabel' })}
          name="name"
          required
        />
      </div>
      <div className={!questions ? undefined : 'ion-hide'}>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.clientRoleRelationship}
          label={intl.formatMessage({ id: 'forms.relationship.label' }, { firstName: careProfile.name })}
          name="clientRoleRelationship"
          options={relationshipOptions}
        />
      </div>
      <div className={!questions || questions?.includes(InitialConsultFields.MARITAL_STATUS) ? undefined : 'ion-hide'}>
        <RadioButtonGroup<FormData>
          allowDeselection
          control={form.control}
          error={form.formState.errors.maritalStatus}
          label={intl.formatMessage({ id: 'models.initialConsultReport.questions.marital_status.content' })}
          name="maritalStatus"
          options={maritalStatusOptions}
        />
      </div>
      <div className={!questions ? undefined : 'ion-hide'}>
        <LocationAutocompleteInput
          control={form.control}
          error={form.formState.errors.placeId}
          label={intl.formatMessage({ id: 'forms.careProfile.locationLabel' })}
          location={{ address: careProfile.location.address, placeId: careProfile.location.placeId }}
          name="placeId"
          required
        />
      </div>
      <div className={!questions ? undefined : 'ion-hide'} data-test="age">
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.age}
          label={intl.formatMessage({ id: 'forms.careProfile.ageLabel' })}
          name="age"
          options={ageOptions}
          required
        />
      </div>
      <div className={!questions ? undefined : 'ion-hide'}>
        <DataSelect<FormData>
          control={form.control}
          error={form.formState.errors.languageCode}
          hasNoneOption
          label={intl.formatMessage({ id: 'forms.careProfile.languageCodeLabel' })}
          name="languageCode"
          type="languages"
        />
      </div>
      <div className={!questions || questions?.includes(InitialConsultFields.RELIGION) ? undefined : 'ion-hide'}>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.religion}
          label={intl.formatMessage({ id: 'models.initialConsultReport.questions.religion.content' })}
          name="religion"
          options={religionOptions}
        />
        <div className={religion === CareProfileReligionEnum.other ? undefined : 'ion-hide'}>
          <Input<FormData>
            control={form.control}
            error={form.formState.errors.religionOther}
            label={intl.formatMessage({ id: 'dictionary.other' })}
            name="religionOther"
            spellcheck
          />
        </div>
      </div>
      <div
        className={
          !questions || questions?.includes(InitialConsultFields.CULTURAL_CONSIDERATIONS) ? undefined : 'ion-hide'
        }
      >
        <Input<FormData>
          control={form.control}
          error={form.formState.errors.culturalConsiderations}
          label={intl.formatMessage({ id: 'models.initialConsultReport.questions.cultural_considerations.content' })}
          name="culturalConsiderations"
          spellcheck
        />
      </div>
      {!questions ? (
        <div className={styles.spaceBetween}>
          <SecondaryButton
            data-test="submit-button"
            disabled={!form?.formState.isValid || form.formState.isSubmitting}
            onClick={() => setBackOrNext(false)}
            textKey="dictionary.previous"
            type="submit"
          />
          <PrimaryButton
            data-test="basic-info-next"
            disabled={!form?.formState.isValid || form.formState.isSubmitting}
            onClick={() => setBackOrNext(true)}
            textKey="dictionary.next"
            type="submit"
          />
        </div>
      ) : (
        <FormButtons hookForm={form} onCancel={onCancel} />
      )}
    </Form>
  );
};

export default InitialConsultBasicInfoForm;
